import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import SVG from '@app/components/SVG';
import { isSwiftCodeValid } from '@app/domain/accountDetailsValidators';
import { getAccountValidationFields } from '@app/domain/accountVerification';
import chevronLeft from '@app/images/chevronLeft.svg';
import { convertToTitleCase } from '@app/utils/stringUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { SergeeSearchResult } from '@mortee/domain/morteeAccountVerification';
import * as React from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AccountVerificationForm, validateIbanCode } from './searchPayeeUtils';

interface MissingFieldsPageProps {
  formData: AccountVerificationForm;
  missingFields: string[];
  setCountryCode: (countryCode: string) => void;
  onSubmit: (data: AccountVerificationForm) => Promise<void>;
  verificationResult: SergeeSearchResult | null;
  onBack: () => void;
}

export default function AccountVerificationInitialMissingFieldsPageV2({
  formData,
  missingFields,
  verificationResult,
  onSubmit,
  onBack,
}: MissingFieldsPageProps): JSX.Element {
  const countryCode = verificationResult?.searchRequest?.account?.countryCode;

  const initialRenderFieldsRef = React.useRef<Array<{ key: string; displayName: string; inputType?: string }>>([]);

  const renderFields = React.useMemo(() => {
    const fields: Array<{ key: string; displayName: string; inputType?: string }> = [];
    let fieldsConfig: ReturnType<typeof getAccountValidationFields> | undefined;
    if (countryCode) {
      fieldsConfig = getAccountValidationFields(countryCode);
    }

    missingFields.forEach((fieldName) => {
      if (!fieldName) return;
      if (fieldName === 'domesticFields' && fieldsConfig) {
        Object.keys(fieldsConfig).forEach((field) => {
          if (formData[field as keyof AccountVerificationForm]) return;
          fields.push({
            key: field,
            displayName: convertToTitleCase(fieldsConfig?.[field]?.displayName),
            inputType: fieldsConfig?.[field]?.inputType,
          });
        });
      } else if (fieldName !== 'domesticFields') {
        fields.push({
          key: fieldName,
          displayName: fieldName === 'iban' ? 'IBAN' : convertToTitleCase(fieldName),
        });
      }
    });

    if (initialRenderFieldsRef.current.length === 0) {
      initialRenderFieldsRef.current = fields;
    }

    return initialRenderFieldsRef.current;
  }, [countryCode, missingFields, formData]);

  const requiredFieldsSchema = React.useMemo(() => {
    const shape: Record<string, any> = {};
    renderFields.forEach((field) => {
      if (field.key === 'iban') {
        shape[field.key] = z
          .string()
          .nonempty(`${field.displayName} is required`)
          .refine(async (value) => await validateIbanCode(value), {
            message: 'Invalid IBAN',
          });
      } else if (field.key === 'swiftBic') {
        shape[field.key] = z
          .string()
          .nonempty(`${field.displayName} is required`)
          .refine((value) => isSwiftCodeValid(value), {
            message: 'Invalid SWIFT/BIC code',
          });
      } else {
        shape[field.key] = z.string().nonempty(`${field.displayName} is required`);
      }
    });
    return z.object(shape);
  }, [renderFields]);

  const form = useForm<AccountVerificationForm>({
    resolver: (zodResolver(requiredFieldsSchema) as unknown) as Resolver<AccountVerificationForm>,
    defaultValues: formData,
  });

  const handleFormSubmit = async (data: AccountVerificationForm): Promise<void> => {
    const mergedData = {
      ...formData,
      ...data,
    };
    await onSubmit(mergedData);
  };

  return (
    <div data-testid='missing-fields-page' id='missing-fields-page' className='min-h-screen bg-background'>
      <div className='w-full max-w-2xl mx-auto p-6 space-y-6 pt-24'>
        <div className='space-y-2'>
          <h1 className='text-2xl font-semibold'>Start a quick check</h1>
          <p className='text-muted-foreground'>Add the additional details to quick check the payee</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className='space-y-6'>
            {renderFields.map((field) => (
              <FormField
                data-testid={`missing-${field.key}`}
                key={field.key}
                control={form.control}
                name={field.key as keyof AccountVerificationForm}
                render={({ field: f }): JSX.Element => (
                  <FormItem>
                    <FormLabel>{field.displayName}</FormLabel>
                    <FormControl>
                      <Input placeholder={`Enter ${field.displayName}`} {...f} className='!text-black' type={field.inputType} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}

            <div className='flex justify-between items-center mt-10'>
              <Button
                data-testid='missing-fields-back-button'
                variant='ghost'
                className='w-fit flex items-center gap-2'
                style={{ color: 'var(--foreground)' }}
                onClick={onBack}
                type='button'
              >
                <SVG accessibilityLabel={'Back'} image={chevronLeft} height={18} className='fill-foreground text-foreground' />{' '}
                Back
              </Button>
              <Button
                data-testid='missing-fields-search-button'
                type='submit'
                variant='default'
                disabled={form.formState.isSubmitting}
                style={{ minWidth: 100 }}
              >
                {form.formState.isSubmitting ? (
                  <span className='animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block'></span>
                ) : (
                  'Search'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
